/**
 * The default error message string displayed when an API call fails with no specific error details.
 */
export const defaultErrorMessage = 'There was a problem completing the requested operation.';

/**
 * Describes the shape of an error object returned from the Infor backend, with optional nested fields for error strings, exception details, and validation problems across two possible payload shapes.
 */
export interface InforErrorObj {
    data?: any;
    payload?: {
        data?: {
            error?: string;
            exception?: {
                message?: string;
            };
            problems?: Record<string, any>;
        };
    };
    error?: string;
    exception?: {
        message?: string;
    };
    problems?: Record<string, any>;
}

/**
 * Extracts error messages from an InforErrorObj, combining them from multiple possible nested locations, and returns a structured response with the main error message and a list of details.
 *
 * The function extracts error messages from multiple possible locations in the response object (payload.data.error, data.error, error) and combines them intelligently. Returns a structured object rather than throwing.
 *
 * @param val - An InforErrorObj extracted from an API response.
 * @returns An object with `error` (the combined error message string, falling back to `defaultErrorMessage`) and `details` (an array of additional detail strings like exception messages or validation problem descriptions).
 */
export const handleReturnInforErrorResponse = (val: InforErrorObj) => {
    const data = val?.data?.error || val?.data;
    const error = val?.payload?.data?.error || val?.error ;
    let errorMessage = defaultErrorMessage;
    if (error && data && typeof data === 'string') {
        errorMessage = `${data} / ${error}`;
    }
    if (!error && data && typeof data === 'string') {
        errorMessage = data;
    }
    if (error && (!data || typeof data !== 'string')) {
        errorMessage = error;
    }
    const exception = val?.payload?.data?.exception?.message || val?.exception?.message || val?.data?.exception?.message;
    const problems = val?.payload?.data?.problems || val?.problems;
    const response: { error: string | undefined; details: Array<string> } = {
        error: errorMessage || defaultErrorMessage,
        details: []
    };
    if (problems) {
        for (const property in problems) {
            response.details.push(`${property}: ${problems[property]}`);
        }
    }
    if (exception) response.details.push(exception);
    return response;
};
